import React from 'react';
import { useRouter } from 'next/navigation';
import debounce from 'lodash/debounce';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import InputBase from '@mui/material/InputBase';
import { styled } from '@mui/material/styles';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import Search from '@mui/icons-material/Search';
import { JobOptionType } from '@/components/job-title/search-area/JobTitleSearch';
import {
  COMPANY_ROUTER_PATH,
  JOB_TITLE_PAGE_ROUTER_PATH,
} from '@/constants/router-path';
import { OFFSET_PIXELS } from '@/components/search/SearchArea';
import { useHasBeenSeen } from '@/utils/hooks';
import { sendEvent, trackClickEvent } from '@/utils/site-track';
import { searchByQuery } from '@/api/tanzhi';

const MainSearch = (props: {
  id?: string;
  value: JobOptionType;
  onChange: (newValue: JobOptionType) => void;
  children?: React.ReactNode;
  showSearchIcon?: boolean;
  className?: string;
  autoClassName?: string;
  paperClassName?: string;
  inputClassName?: string;
  isNeedSearch?: boolean;
  searchError?: boolean;
  mountedSearch?: boolean;
  multiSearch?: boolean;
  dataEleId?: string;
  dataEleGroup?: string;
  dataObserverId?: string;
  searchType?: string;
}) => {
  const {
    id,
    value,
    children = <></>,
    showSearchIcon = true,
    className = '',
    autoClassName = '',
    paperClassName = '',
    inputClassName = '',
    isNeedSearch = true,
    onChange,
    searchError = '',
    mountedSearch,
    multiSearch,
    dataEleId,
    dataEleGroup,
    dataObserverId,
    searchType = 'data_search',
  } = props;
  const [options, setOptions] = React.useState<JobOptionType[]>([]);
  const router = useRouter();

  const inputRef = React.useRef<HTMLInputElement>();

  const inputDisappearHandler = () => {
    inputRef.current?.blur();
  };

  useHasBeenSeen(inputRef, () => {}, inputDisappearHandler, OFFSET_PIXELS);

  const searchAndSetOptions = (searchText: string) => {
    searchByQuery(searchText.trim(), searchType).then((res) => {
      if (res.status === 200) {
        const options = res.data.map(
          ({
            id,
            name,
            alias_id: aliasId,
            name_type: nameType,
            city,
            search_type,
          }: {
            id: string;
            name: string;
            alias_id: string;
            name_type: string;
            city: { name: string; pinyin: string };
            search_type: string;
          }) => {
            return { name, id, aliasId, nameType, city, search_type };
          },
        );
        setOptions(options);
      }
    });
  };

  const handleEnterSearch = async () => {
    if (isNeedSearch) {
      let opt = options[0];
      if (value && value.id) {
        opt = value;
      }
      if (!opt) return;
      const searchType = opt['search_type'];
      await sendEvent({
        name: `search_${searchType ? searchType : 'job_title'}`,
        search_keyword: opt.name,
      });
      if (!searchType || searchType === 'job_title') {
        router.push(
          `${JOB_TITLE_PAGE_ROUTER_PATH}${opt.nameType == 'alias' ? (opt.aliasId ? `${opt.aliasId}/` : `${opt.id}/?jn=${encodeURIComponent(opt.name)}`) : `${opt.id}/`}`,
        );
      } else if (searchType === 'job_title_city') {
        const pinyin = opt.city?.pinyin;
        pinyin &&
          router.push(
            `/${pinyin}/${opt.nameType == 'alias' ? (opt.aliasId ? `${opt.aliasId}/` : `${opt.id}/?jn=${encodeURIComponent(opt.name)}`) : `${opt.id}/`}`,
          );
      } else {
        router.push(`${COMPANY_ROUTER_PATH}${opt.id}/`);
      }
    }
  };

  const styledPopper = styled(Popper)({
    paddingTop: '4px',
    [`.${autocompleteClasses.listbox} .${autocompleteClasses.option}.${autocompleteClasses.focused}`]:
      {
        color: '#1861bf',
        background: 'rgba(24, 97, 191, 0.1)',
      },
  });

  const handleSearch = React.useCallback(
    debounce((e, value) => {
      searchAndSetOptions(value);
    }, 300),
    [],
  );

  React.useEffect(() => {
    if (mountedSearch) {
      searchAndSetOptions('');
    }
  }, []);

  React.useEffect(() => {
    if (value && value.id) {
      handleEnterSearch();
    }
  }, [value]);

  return (
    <div className={`flex flex-col ${className}`}>
      <Autocomplete
        id={id}
        data-observer-id={dataObserverId}
        data-ele-group={dataEleGroup}
        data-ele-id={dataEleId}
        onFocus={(e) => dataEleId && trackClickEvent(e)}
        onInputChange={handleSearch}
        value={value}
        PopperComponent={styledPopper}
        componentsProps={{
          popper: {
            modifiers: [
              {
                name: 'flip',
                enabled: false,
              },
            ],
          },
        }}
        onChange={(event, newValue) => onChange(newValue)}
        filterOptions={(options) => options}
        getOptionLabel={(option) => option.name}
        autoHighlight={true}
        selectOnFocus={false}
        disableClearable={true}
        blurOnSelect={true}
        renderInput={(params) => (
          <Paper
            className={`!text-left !flex !items-center [&.MuiPaper-root]:!shadow-none ${paperClassName} tablet:[&.MuiPaper-root]:!shadow-[0_3px_20px_rgba(0,0,0,0.05)]`}
            ref={params.InputProps.ref}
          >
            {showSearchIcon && (
              <Search className="!mr-3.5 !text-2.5xl tablet:!text-3.25xl" />
            )}
            <InputBase
              inputRef={inputRef}
              placeholder={multiSearch ? '搜索职场薪资·公司' : '搜索'}
              data-error={searchError}
              className={`!text-black !w-full ${inputClassName}`}
              inputProps={{
                ...params.inputProps,
                className:
                  params.inputProps.className +
                  ' !p-0 animate-none focus:animate-[opacity_0.01s] tablet:focus:animate-none',
              }}
            />
          </Paper>
        )}
        options={options}
        noOptionsText="抱歉，暂未搜索到相关职位"
        className={autoClassName}
        isOptionEqualToValue={(option, value) =>
          option &&
          value &&
          !!option.id &&
          !!value.id &&
          option.name === value.name &&
          option.id === value.id
        }
      />
      {children}
    </div>
  );
};

export default MainSearch;
